// Remaps a legacy Dugout drawing's asset URLs onto the rinus `assets/dugout/` namespace, so existing
// Dugout drawings (Fabric 2.3.x–2.7.x) import and re-export against the copied-verbatim asset set.
// Unlike `convertAssetsToFrf` (a per-colour shirt swap), this is a GENERIC prefix rewrite: Dugout
// references every asset by a build path that varies across exports — `/static/media/x`,
// `./static/media/x`, even `/../static/media/x` — while rinus serves the identical files under
// `assets/dugout/`. So rewrite any `…static/media/<file>` → `assets/dugout/<file>`, everywhere it
// appears (both icon shapes + the compact sourcePath), for every object.
import type { SerializedDrawingObject, SerializedIcon } from '@/types/drawing';

const DUGOUT_ASSET_DIR = 'assets/dugout/';
// Match up to and including `static/media/`, whatever the leading prefix (`/`, `./`, `/../`, a full
// origin) — replacing it with the rinus namespace leaves the bare filename intact.
const MEDIA_PATH = /^.*static\/media\//;

const remap = (path: string): string =>
  // Function replacer: returns the namespace literally (no `$`-group substitution in the replacement).
  MEDIA_PATH.test(path)
    ? path.replace(MEDIA_PATH, () => DUGOUT_ASSET_DIR)
    : path;

// `defaultIcon` / `alternateIcon` are polymorphic: a bare URL string OR an `{ src }` object. Rewrite
// whichever shape the drawing used, preserving it.
const remapIcon = (
  icon: SerializedIcon | undefined,
): SerializedIcon | undefined => {
  if (typeof icon === 'string') return remap(icon);
  if (icon && typeof icon === 'object' && typeof icon.src === 'string')
    return { ...icon, src: remap(icon.src) };
  return icon;
};

const remapObject = (
  item: SerializedDrawingObject,
): SerializedDrawingObject => {
  const out: SerializedDrawingObject = { ...item };
  if (out.defaultIcon !== undefined)
    out.defaultIcon = remapIcon(out.defaultIcon);
  if (out.alternateIcon !== undefined)
    out.alternateIcon = remapIcon(out.alternateIcon);
  // `objects` is either the compact sourcePath string (rewrite it) or a nested child array (recurse —
  // Dugout groups nest the SVG-sourced child one level down, `objects[0].objects`).
  if (typeof out.objects === 'string') out.objects = remap(out.objects);
  else if (Array.isArray(out.objects))
    out.objects = out.objects.map((child) => remapObject(child));
  return out;
};

const convertAssetsToDugout = (
  objects: SerializedDrawingObject[],
): SerializedDrawingObject[] => objects.map((item) => remapObject(item));

// Exposed so importJSON can rewrite the top-level `backgroundImage.src` too — it lives outside
// `objects[]`, and if left as a legacy `/static/media/*` path its enliven 404s and aborts the whole
// loadFromJSON (empty canvas). The field is re-derived + reloaded from config afterwards, but the
// import must still enliven a VALID background src to succeed.
export const remapDugoutAsset = remap;

export default convertAssetsToDugout;
